import { FormEvent } from 'react'
import { FaSearch } from 'react-icons/fa'
import { Banner, Button, CollapsibleSection, DatePicker, Field, FieldGrid, Input, Toolbar } from '../../../../../ui'
import { formatDate, toIsoDate } from '../shared'
import s from './styles.module.scss'

export interface SearchCriteria {
    firstName: string
    lastName: string
    phone: string
    email: string
    dob: Date | null
}

export type SearchErrors = Partial<Record<keyof SearchCriteria | 'form', string>>

export const EMPTY_CRITERIA: SearchCriteria = { firstName: '', lastName: '', phone: '', email: '', dob: null }

interface SearchPanelProps {
    criteria: SearchCriteria
    errors: SearchErrors
    loading: boolean
    /** Collapsed once a result is on screen; the header still shows what was searched. */
    open: boolean
    onToggle: (open: boolean) => void
    onChange: (patch: Partial<SearchCriteria>) => void
    onClear: () => void
    onSubmit: () => void
}

/** One-line description of the active criteria, shown in the collapsed header. */
export const describeCriteria = (c: SearchCriteria): string => {
    const parts: string[] = []
    const name = `${c.firstName.trim()} ${c.lastName.trim()}`.trim()
    if (name) parts.push(name)
    if (c.phone.trim()) parts.push(c.phone.trim())
    if (c.email.trim()) parts.push(c.email.trim())
    if (c.dob) parts.push(`DOB ${formatDate(toIsoDate(c.dob) as string)}`)
    return parts.join(' · ')
}

export const SearchPanel = ({
    criteria,
    errors,
    loading,
    open,
    onToggle,
    onChange,
    onClear,
    onSubmit,
}: SearchPanelProps) => {
    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        onSubmit()
    }

    const summary = describeCriteria(criteria)

    return (
        <CollapsibleSection
            variant="panel"
            level="h3"
            title="Search criteria"
            meta={!open && summary ? summary : undefined}
            open={open}
            onToggle={onToggle}
        >
            <form onSubmit={handleSubmit} className={s.searchForm} noValidate>
                {errors.form && <Banner tone="bad">{errors.form}</Banner>}

                <FieldGrid minColumn={200}>
                    <Field
                        label="First name"
                        error={errors.firstName}
                        hint="Required with last name when searching by name"
                    >
                        {(id) => (
                            <Input
                                id={id}
                                autoComplete="off"
                                value={criteria.firstName}
                                invalid={!!errors.firstName}
                                onChange={(e) => onChange({ firstName: e.target.value })}
                                placeholder="e.g. Amelia"
                            />
                        )}
                    </Field>
                    <Field label="Last name" error={errors.lastName}>
                        {(id) => (
                            <Input
                                id={id}
                                autoComplete="off"
                                value={criteria.lastName}
                                invalid={!!errors.lastName}
                                onChange={(e) => onChange({ lastName: e.target.value })}
                                placeholder="e.g. Hart"
                            />
                        )}
                    </Field>
                    <Field label="Phone number" error={errors.phone} hint="UK number with +44, e.g. +447123456789">
                        {(id) => (
                            <Input
                                id={id}
                                type="tel"
                                autoComplete="off"
                                value={criteria.phone}
                                invalid={!!errors.phone}
                                onChange={(e) => onChange({ phone: e.target.value })}
                                placeholder="+44…"
                            />
                        )}
                    </Field>
                    <Field label="Email address" error={errors.email}>
                        {(id) => (
                            <Input
                                id={id}
                                type="email"
                                autoComplete="off"
                                value={criteria.email}
                                invalid={!!errors.email}
                                onChange={(e) => onChange({ email: e.target.value })}
                                placeholder="client@example.com"
                            />
                        )}
                    </Field>
                    <Field label="Date of birth" optional hint="Narrows the match when several clients share details">
                        <DatePicker
                            selected={criteria.dob}
                            onChange={(d) => onChange({ dob: (d as Date | null) ?? null })}
                            maxDate={new Date()}
                            isClearable
                        />
                    </Field>
                </FieldGrid>

                <Toolbar
                    end={
                        <>
                            <Button type="button" onClick={onClear} disabled={loading}>
                                Clear
                            </Button>
                            <Button type="submit" variant="primary" icon={<FaSearch />} loading={loading}>
                                Search
                            </Button>
                        </>
                    }
                >
                    <span className={s.formNote}>
                        Search by full name, phone number or email address. Combine fields to narrow the match; every
                        introducer database is checked.
                    </span>
                </Toolbar>
            </form>
        </CollapsibleSection>
    )
}
